// OverlayToolbar.js
// Aktive-Overlay-Toolbar (Admin): kleines Bedien-Popup unter dem aktiven Overlay. Als OverlayView umgesetzt, damit es
// sich via draw() automatisch unter der Box positioniert und bei Pan/Zoom mitwandert. Gedacht als EINE geteilte
// Instanz pro Karte: attachTo(editor) richtet sie auf das aktuell gewählte Overlay aus; show()/hide() koppeln an die Auswahl.
//
// Inhalt (Layout-Vorbild: footer_fill_all): links Modus-Toggle (Skalieren/Rotieren/Verzerren), rechts OK (speichern)
// und X (auf Ausgangszustand). Die Buttons melden nur über Callbacks (onMode/onSave/onReset) — den Pressed-State setzt
// der Host via setMode() (gespeist aus dem onModeChange des Editors) → eine Wahrheitsquelle, keine Doppelzustände.
//
// google wird zur Laufzeit injiziert (wie OverlayEditor), weil OverlayView erst nach API-Load existiert.

// topo2 Button-Basis (eckig, kein Schatten, Outline/Solid via [aria-pressed]) — co-located, reist mit dem Admin-Bundle.

// Inline-SVG-Icons (stroke:currentColor, Stil wie die documents-Footer-Buttons). Tooltips kommen über str().
import { str } from "../../i18n/str.js";

// titleKey statt title: str() wird erst zur Build-Zeit (im Toolbar-Aufbau) aufgelöst, NICHT beim Modul-Load
// (sonst zu früh = roher Key eingefroren).
const MODE_BUTTONS = [
    { key: 'scale',  titleKey: 'OVERLAY_MODE_SCALE', svg: '<rect x="4" y="4" width="16" height="16" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"/>' },
    { key: 'rotate', titleKey: 'OVERLAY_MODE_ROTATE', svg: '<path d="M19 12a7 7 0 1 1-2-4.9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M17 4v4h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' },
    { key: 'skew',   titleKey: 'OVERLAY_MODE_SKEW', svg: '<path d="M8 5h12l-4 14H4z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' }
];
// Sub-Toolbar (Chef 17): Undo/Redo (gebogene Pfeile) + Verzerrung-löschen (Parallelogramm mit Schrägstrich).
const UNDO_SVG  = '<path d="M9 14L4 9l5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 9h10a6 6 0 0 1 0 12H8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
const REDO_SVG  = '<path d="M15 14l5-5-5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H10a6 6 0 0 0 0 12h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
const CLEAR_SVG = '<path d="M8 6h11l-3 12H5z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M5 18L19 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';
// Chef 19: „von der Karte entfernen" — Exit-Symbol (Tür + Pfeil raus).
const EXIT_SVG  = '<path d="M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 8l4 4-4 4M20 12H9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';

// Untergrenze der Toolbar: Abstand zur Karten-Unterkante, wenn die Bild-Unterkante darunter liegt.
const BOTTOM_PADDING_PX = 10;
// Luft zwischen Anker (Bild-Unterkante) und Toolbar-Oberkante.
const ANCHOR_GAP_PX = 8;

export function createOverlayToolbarClass(google) {
    return class OverlayToolbar extends google.maps.OverlayView {
        constructor(map, opts = {}) {
            super();
            this._map = map;
            this._onMode = typeof opts.onMode === 'function' ? opts.onMode : null;
            this._onSave = typeof opts.onSave === 'function' ? opts.onSave : null;
            this._onReset = typeof opts.onReset === 'function' ? opts.onReset : null;
            this._onUndo = typeof opts.onUndo === 'function' ? opts.onUndo : null;            // Chef 17
            this._onRedo = typeof opts.onRedo === 'function' ? opts.onRedo : null;            // Chef 17
            this._onClearDistort = typeof opts.onClearDistort === 'function' ? opts.onClearDistort : null;  // Chef 17
            this._onRemoveFromMap = typeof opts.onRemoveFromMap === 'function' ? opts.onRemoveFromMap : null;  // Chef 19
            this._onOpacity = typeof opts.onOpacity === 'function' ? opts.onOpacity : null;  // Chef 19: Deckkraft-Slider
            this._editor = null;            // aktuell angeheftetes OverlayEditor-Handle (oder null)
            this._mode = 'scale';
            this._prevMode = 'scale';       // Chef 18: letzter Nicht-Skew-Modus → Rücksprung beim Skew-Toggle
            this._visible = false;
            this._div = null;
            this._modeButtons = {};
            this._subPanel = null;          // Chef 17: Sub-Toolbar (Undo/Redo/Löschen), nur im Skew-Modus
            this._undoBtn = null;
            this._redoBtn = null;
            this._posLabel = null;          // Chef 17: Positions-Anzeige „pos / len" zwischen den Pfeilen
            this._opaSlider = null;         // Chef 19: horizontaler Deckkraft-Slider (Reihe 3)
            this.setMap(map);
        }

        onAdd() {
            // Container: positioniert (draw), horizontal zentriert unter der Box, VERTIKALE Stapelung (Haupt- + Sub-Toolbar).
            const div = document.createElement('div');
            div.className = 'overlayToolbar';
            div.style.position = 'absolute';
            div.style.display = 'none';                       // bis show() + draw()
            // Zentrierung + Versatz rechnet draw() in left/top aus (früher `transform: translate(-50%, 8px)`) —
            // so steht die 8px-Luft sichtbar in der Clamp-Rechnung, statt versteckt in einer CSS-Transform.
            div.style.flexDirection = 'column';
            div.style.alignItems = 'center';
            // MUSS über dem aktiven Overlay liegen: dessen Div + Handles haben ACTIVE_Z_INDEX (100000) in DERSELBEN Pane
            // (overlayMouseTarget). Mit z-index:5 verdeckten gestapelte Overlays/Handles die Toolbar → unklickbar.
            div.style.zIndex = '200000';
            // Klicks/Drags nicht an die Karte durchreichen (sonst Deselect/Pan). dblclick eigens stoppen: schnelles
            // Umschalten am Button feuert sonst einen Doppelklick, der in die Karte blubbert → Google zoomt.
            div.addEventListener('mousedown', (e) => e.stopPropagation());
            div.addEventListener('click', (e) => e.stopPropagation());
            div.addEventListener('dblclick', (e) => { e.stopPropagation(); e.preventDefault(); });

            // Reihe 1 (Haupt-Panel): links Modus-Umschalter, rechts OK/X.
            const panel = this._makePanel();
            const left = document.createElement('span');
            left.style.display = 'inline-flex';
            left.style.alignItems = 'center';
            for (const m of MODE_BUTTONS) {
                // Chef 18: vertikaler Trenner VOR „Verzerren" (trennt Skew von Skalieren/Rotieren).
                if (m.key === 'skew') {
                    const sep = document.createElement('span');
                    sep.className = 'overlayToolbarSep';
                    sep.style.cssText = 'display:inline-block;width:1px;align-self:stretch;background:#ddd;margin:2px 5px;';
                    left.appendChild(sep);
                }
                const b = this._makeButton(str(m.titleKey), m.svg);
                b.addEventListener('click', () => this._handleModeClick(m.key));
                this._modeButtons[m.key] = b;
                left.appendChild(b);
            }
            const right = document.createElement('span');
            right.style.display = 'inline-flex';
            right.style.marginLeft = '10px';
            // Chef-Charge 5: „OK"-Text-Button (statt ✓-Haken) — Vorbild documents fill_all-Footer. Quadratisch (--icon)
            // wie die anderen Toolbar-Buttons, nur Text statt SVG.
            const ok = document.createElement('button');
            ok.type = 'button';
            ok.className = 'overlayToolbarBtn topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--outline';
            ok.title = str('OVERLAY_TOOLBAR_SAVE');
            ok.style.margin = '0 1px';
            ok.textContent = str('OVERLAY_TOOLBAR_OK');
            ok.addEventListener('click', () => { if (this._onSave) { this._onSave(); } });
            const x = this._makeButton(str('OVERLAY_TOOLBAR_RESET'), UNDO_SVG);   // Chef: Rückwärtspfeil statt Kreuz
            x.addEventListener('click', () => { if (this._onReset) { this._onReset(); } });
            // Chef 19: „von der Karte entfernen" — ganz am Schluss der ersten Reihe.
            const exit = this._makeButton(str('OVERLAY_REMOVE_FROM_MAP'), EXIT_SVG);
            exit.addEventListener('click', () => { if (this._onRemoveFromMap) { this._onRemoveFromMap(); } });
            right.appendChild(ok);
            right.appendChild(x);
            right.appendChild(exit);
            panel.appendChild(left);
            panel.appendChild(right);

            // Reihe 2 (Sub-Toolbar, Chef 17): Undo/Redo + Verzerrung-löschen — nur im Skew-Modus sichtbar (_applyMode).
            const sub = this._makePanel();
            sub.style.marginTop = '6px';
            sub.style.display = 'none';
            this._undoBtn = this._makeButton(str('OVERLAY_UNDO'), UNDO_SVG);
            this._undoBtn.addEventListener('click', () => { if (this._onUndo) { this._onUndo(); } });
            this._redoBtn = this._makeButton(str('OVERLAY_REDO'), REDO_SVG);
            this._redoBtn.addEventListener('click', () => { if (this._onRedo) { this._onRedo(); } });
            // Chef 17: Positions-Anzeige „pos / len" zwischen Zurück und Vorwärts → man sieht, wo man in der History steht.
            this._posLabel = document.createElement('span');
            this._posLabel.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;min-width:38px;'
                + 'padding:0 6px;font:12px sans-serif;color:#444;white-space:nowrap;';
            this._posLabel.textContent = '–';
            const subSep = document.createElement('span');
            subSep.style.cssText = 'display:inline-block;width:1px;align-self:stretch;background:#ddd;margin:2px 5px;';
            const clear = this._makeButton(str('OVERLAY_CLEAR_DISTORT'), CLEAR_SVG);
            clear.addEventListener('click', () => { if (this._onClearDistort) { this._onClearDistort(); } });
            sub.appendChild(this._undoBtn);
            sub.appendChild(this._posLabel);
            sub.appendChild(this._redoBtn);
            sub.appendChild(subSep);
            sub.appendChild(clear);
            this._subPanel = sub;

            // Reihe 3 (Chef 19): Deckkraft-Slider (horizontal, frontend-Style) — LETZTES Element, immer sichtbar solange die
            // Toolbar offen ist. Die Sub-Toolbar (Reihe 2) liegt dazwischen (erscheint nur im Skew-Modus). Alles hängt am
            // unteren Anker → stabil gegen die gezoomte Overlay-Größe.
            const opa = this._makePanel();
            opa.style.marginTop = '6px';
            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = '0';
            slider.max = '100';
            slider.value = '90';
            slider.title = str('OVERLAY_OPACITY');
            slider.className = 'topo2-range';   // Chef-Charge 6: geteilter eckiger Slider (kein Browser-Rundung, Akzent-Thumb)
            slider.style.cssText = 'width:140px;';
            slider.addEventListener('input', () => { if (this._onOpacity) { this._onOpacity(parseInt(slider.value, 10)); } });
            opa.appendChild(slider);
            this._opaSlider = slider;

            div.appendChild(panel);
            div.appendChild(sub);
            div.appendChild(opa);
            this._div = div;
            this._applyMode();
            // NICHT in die Pane hängen: die Toolbar ist ein KIND des Editor-Divs (wie die Griffe) und wandert
            // damit beim Pan/Zoom automatisch mit dem Bild mit. Das Einhängen macht _mountIntoEditor().
            this._mountIntoEditor();
            // EIN Korrektur-Durchgang für die KLEMMUNG (nicht für die Position — die sitzt als Kind fest).
            // Warum so und nicht anders, aus der Fehlersuche:
            //   - Während einer Bewegung misst _applyBottomClamp() im draw() den laufenden Frame korrekt.
            //   - Am ENDE eines Pans nicht: Google entfernt den Pane-Transform erst NACH dem Zeichen-Durchgang,
            //     die Messung liefert dort die alte Bildschirmlage → Versatz um die Pan-Strecke.
            //   - Ein rAF pro draw() behob das Ende, schrieb aber pro Frame einen zweiten, um einen Frame alten
            //     Wert → sichtbares Aufblitzen beim Ziehen.
            //   - `idle` allein ist zu früh: da liegt der Transform noch an.
            // Also: bei `idle` auslösen (genau einmal, kein zweiter Schreiber pro Frame), aber einen Frame später
            // messen — dann ist der Transform weg und der Rahmen steht.
            this._idleClamp = google.maps.event.addListener(this._map, 'idle', () => {
                if (this._clampRaf) { cancelAnimationFrame(this._clampRaf); }
                this._clampRaf = requestAnimationFrame(() => {
                    this._clampRaf = null;
                    if (this._div && this._visible) { this._applyBottomClamp(); }
                });
            });
        }

        // Panel (eine Toolbar-Reihe): eckig, KEIN Schatten (Chef); dünne Kontur zur Abgrenzung von der Karte.
        _makePanel() {
            const p = document.createElement('div');
            p.style.display = 'inline-flex';
            p.style.alignItems = 'center';
            p.style.whiteSpace = 'nowrap';
            p.style.padding = '4px';
            p.style.background = '#fff';
            p.style.border = '1px solid #ccc';
            p.style.borderRadius = '0';
            return p;
        }

        // topo2-Button: eckig, kein Schatten, Outline-Look. Aktiv (Modus) setzt der Host via [aria-pressed] → Solid
        // (gleicher Look-Override wie im Frontend, kein Klassenwechsel). --small = 30×30.
        _makeButton(title, svgInner) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'overlayToolbarBtn topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--outline';
            b.title = title;   // bereits übersetzt: jeder Aufrufer übergibt str(...)
            b.style.margin = '0 1px';
            b.innerHTML = '<svg viewBox="0 0 24 24">' + svgInner + '</svg>';
            return b;
        }

        // Position RELATIV zum Editor-Div, dessen Kind die Toolbar ist — keine Projektion, kein Wechsel zwischen
        // Div- und Container-Pixeln. Der Anker ist die Unterkante der TATSÄCHLICH gerenderten (rotierten/verzerrten)
        // Form; beim Pan/Zoom muss hier gar nichts passieren, das erledigt der gemeinsame Elternknoten.
        draw() {
            if (!this._div) { return; }
            const local = (this._editor && this._editor.getRenderedBottomAnchorLocal) ? this._editor.getRenderedBottomAnchorLocal() : null;
            if (!this._visible || !local) { this._div.style.display = 'none'; return; }
            this._mountIntoEditor();            // Editor gewechselt? dann umhängen
            this._div.style.display = 'flex';   // VOR dem Messen: bei display:none sind offsetWidth/Height 0
            this._div.style.left = Math.round(local.x - this._div.offsetWidth / 2) + 'px';
            this._baseTop = Math.round(local.y + ANCHOR_GAP_PX);   // ungeklemmt — Bezug für jede Klemmung
            this._div.style.top = this._baseTop + 'px';
            this._applyBottomClamp();
        }

        // Toolbar ins Editor-Div hängen (wie die Griffe). Idempotent; greift auch, wenn attachTo() vor onAdd() kam.
        _mountIntoEditor() {
            const host = (this._editor && this._editor.getElement) ? this._editor.getElement() : null;
            if (!this._div || !host) { return; }
            if (this._div.parentNode !== host) { host.appendChild(this._div); }
        }

        // Untergrenze: bei großen Bildern liegt die Bild-Unterkante unter dem Sichtbereich — dann die Toolbar an der
        // Karten-Unterkante halten. Gemessen an echten Bildschirm-Rechtecken, ohne Koordinatensystem-Wechsel.
        _applyBottomClamp() {
            const mapDiv = (this._map && this._map.getDiv) ? this._map.getDiv() : null;
            if (!mapDiv || this._baseTop == null) { return; }
            // IMMER von der ungeklemmten Basis aus rechnen (nicht von der zuletzt gesetzten Position). Sonst wäre die
            // Korrektur nur in eine Richtung möglich: eine einmal zu weit nach oben geschobene Toolbar bliebe oben.
            if (parseFloat(this._div.style.top) !== this._baseTop) { this._div.style.top = this._baseTop + 'px'; }
            const limit = mapDiv.getBoundingClientRect().bottom - BOTTOM_PADDING_PX;
            const over = this._div.getBoundingClientRect().bottom - limit;
            if (over > 0) { this._div.style.top = Math.round(this._baseTop - over) + 'px'; }
        }

        // Modus-Klick: normalerweise den Modus melden. Chef 18: „Verzerren" TOGGLET — nochmaliger Klick auf den bereits
        // aktiven Skew-Button kehrt zum gemerkten vorigen Modus (Skalieren/Rotieren) zurück.
        _handleModeClick(key) {
            if (!this._onMode) { return; }
            if (key === 'skew' && this._mode === 'skew') { this._onMode(this._prevMode || 'scale'); }
            else { this._onMode(key); }
        }

        // Auf ein Overlay ausrichten (oder lösen mit null). Übernimmt dessen aktuellen Modus.
        attachTo(editor) {
            this._editor = editor || null;
            this._mountIntoEditor();
            if (this._editor && this._editor.getMode) { this._applyModeState(this._editor.getMode()); }
            if (this._editor && this._editor.getOpacity) { this.setOpacity(this._editor.getOpacity()); }   // Chef 19
        }

        // Deckkraft-Slider auf die aktuelle Overlay-Deckkraft setzen (beim Anheften gespiegelt).
        setOpacity(pct) {
            if (this._opaSlider) { this._opaSlider.value = String(Math.round(pct)); }
        }

        // Pressed-State setzen (vom Host aus dem onModeChange des Editors gespeist).
        setMode(mode) {
            this._applyModeState(mode);
        }

        // Modus + Pressed-State setzen; den letzten NICHT-Skew-Modus merken (für den Skew-Toggle-Rücksprung, Chef 18).
        _applyModeState(mode) {
            if (mode !== 'skew') { this._prevMode = mode; }
            this._mode = mode;
            this._applyMode();
        }

        _applyMode() {
            // Aktiver Modus = [aria-pressed="true"] → topo2-Solid-Override (gleicher Look wie Frontend, kein Klassenwechsel).
            for (const key in this._modeButtons) {
                this._modeButtons[key].setAttribute('aria-pressed', key === this._mode ? 'true' : 'false');
            }
            // Chef 17: Sub-Toolbar (Undo/Redo/Löschen) nur im Verzerren-Modus.
            if (this._subPanel) { this._subPanel.style.display = (this._mode === 'skew') ? 'inline-flex' : 'none'; }
        }

        // Undo/Redo-Buttons aktivieren/deaktivieren + Positions-Anzeige (vom Host aus onHistoryChange des Editors gespeist).
        setHistoryState(canUndo, canRedo, pos, len) {
            const apply = (btn, on) => {
                if (!btn) { return; }
                btn.disabled = !on;
                btn.style.opacity = on ? '1' : '0.4';
                btn.style.cursor = on ? 'pointer' : 'default';
            };
            apply(this._undoBtn, !!canUndo);
            apply(this._redoBtn, !!canRedo);
            if (this._posLabel) { this._posLabel.textContent = (typeof len === 'number') ? (pos + ' / ' + len) : '–'; }
        }

        show() { this._visible = true; this.draw(); }
        hide() { this._visible = false; if (this._div) { this._div.style.display = 'none'; } }

        remove() { this.setMap(null); }

        onRemove() {
            if (this._idleClamp) { google.maps.event.removeListener(this._idleClamp); this._idleClamp = null; }
            if (this._clampRaf) { cancelAnimationFrame(this._clampRaf); this._clampRaf = null; }
            if (this._div && this._div.parentNode) { this._div.parentNode.removeChild(this._div); }
            this._div = null;
            this._modeButtons = {};
            this._subPanel = null;
            this._undoBtn = null;
            this._redoBtn = null;
            this._posLabel = null;
            this._opaSlider = null;
        }
    };
}
